import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Chart } from 'chart.js';
import {
    createStackedVerticalBarChart,
    StackedVerticalBarChartData,
    StackedVerticalBarChartTooltipFooterFormatter,
    StackedVerticalBarChartTooltipLabelFormatter,
} from './util/StackedVerticalBarChart';
import { useBarChartScrollContainer } from '@/features/charts/useBarChartScrollContainer';
import chartIcon from '@/assets/images/sidebar/analysis_icon.png';
import { useTranslation } from 'react-i18next';

/**
 * 積み上げ縦棒グラフ
 */
type StackedVerticalBarChartProps = {
    data: StackedVerticalBarChartData;
    resolveColor?: (
        label: string,
        index: number,
    ) => Promise<string | undefined> | string | undefined;
    tooltipLabelFormatter?: StackedVerticalBarChartTooltipLabelFormatter;
    tooltipFooterFormatter?: StackedVerticalBarChartTooltipFooterFormatter;
    yAxisMax?: number;
    yAxisStepSize?: number;
    yAxisSuggestedMax?: number;
    formatYAxisTick?: (value: number) => string;
    noDataMessage?: string;
    onSwitchToDonut?: () => void;
    hideSwitchButton?: boolean;
    hideLegendButton?: boolean;
    legendOpen?: boolean;
    onToggleLegend?: () => void;
    legendAnchorRef?: React.RefObject<HTMLElement | null>;
};

const generateFallbackColors = (count: number): string[] =>
    Array.from({ length: count }, (_, index) => {
        const hue = (index * 360) / Math.max(count, 1);
        return `hsl(${hue}, 60%, 72%)`;
    });

const StackedVerticalBarChart = ({
    data,
    resolveColor,
    tooltipLabelFormatter,
    tooltipFooterFormatter,
    yAxisMax,
    yAxisStepSize,
    yAxisSuggestedMax,
    formatYAxisTick,
    noDataMessage,
    onSwitchToDonut,
    hideSwitchButton,
    hideLegendButton,
    legendOpen,
    onToggleLegend,
    legendAnchorRef,
}: StackedVerticalBarChartProps) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const chartRef = useRef<Chart<'bar'> | null>(null);
    const { t } = useTranslation('analysis');
    const labelCount = data?.labels?.length ?? 0;
    const { scrollRef, innerWidthPx, containerWidth, minRequiredWidthPx } =
        useBarChartScrollContainer(labelCount);
    const [showLegend, setShowLegend] = useState(false);
    const effectiveShowLegend = legendOpen ?? showLegend;
    const toggleLegend = () => {
        if (onToggleLegend) {
            onToggleLegend();
        } else {
            setShowLegend((v) => !v);
        }
    };
    const [legendColors, setLegendColors] = useState<string[]>([]);
    const legendButtonRef = useRef<HTMLButtonElement | null>(null);
    const [positionTick, setPositionTick] = useState(0);
    const [, setLegendRefreshTick] = useState(0);
    useEffect(() => {
        if (!effectiveShowLegend) return;
        const handler = () => setPositionTick((v) => v + 1);
        window.addEventListener('scroll', handler, true);
        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('scroll', handler, true);
            window.removeEventListener('resize', handler);
        };
    }, [effectiveShowLegend]);

    useEffect(() => {
        if (!canvasRef.current) return;

        chartRef.current?.destroy();

        if (!data || !data.datasets.length) return;

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) return;

        let isMounted = true;

        const run = async () => {
            const fallbackColors = generateFallbackColors(Math.max(data.datasets.length, 1));

            const enrichedDatasets = await Promise.all(
                data.datasets.map(async (dataset, index) => {
                    if (dataset.backgroundColor) {
                        return dataset;
                    }
                    const targetKey = dataset.id ?? dataset.label;
                    let resolved: string | undefined;
                    try {
                        resolved = (await resolveColor?.(targetKey, index)) ?? undefined;
                    } catch {
                        resolved = undefined;
                    }
                    return {
                        ...dataset,
                        backgroundColor: resolved ?? fallbackColors[index % fallbackColors.length],
                    };
                }),
            );

            const chart = createStackedVerticalBarChart(
                ctx,
                {
                    ...data,
                    datasets: enrichedDatasets,
                },
                {
                    yAxisMax,
                    yAxisStepSize,
                    yAxisSuggestedMax,
                    formatYAxisTick,
                    formatTooltipLabel: tooltipLabelFormatter,
                    formatTooltipFooter: tooltipFooterFormatter,
                },
            );

            if (!isMounted) {
                chart?.destroy();
                return;
            }
            chartRef.current = chart ?? null;
        };

        run();

        return () => {
            isMounted = false;
            chartRef.current?.destroy();
            chartRef.current = null;
        };
    }, [
        data,
        resolveColor,
        tooltipFooterFormatter,
        tooltipLabelFormatter,
        yAxisMax,
        yAxisStepSize,
        yAxisSuggestedMax,
        formatYAxisTick,
    ]);

    useEffect(() => {
        chartRef.current?.resize();
    }, [innerWidthPx]);

    // 凡例表示用の色（チャートと同じロジックで解決）
    useEffect(() => {
        let cancelled = false;
        const buildLegendColors = async () => {
            if (!data || !data.datasets.length) {
                setLegendColors([]);
                return;
            }
            const fallbackColors = generateFallbackColors(Math.max(data.datasets.length, 1));
            const colors = await Promise.all(
                data.datasets.map(async (dataset, index) => {
                    if (dataset.backgroundColor) {
                        return String(dataset.backgroundColor);
                    }
                    const key = dataset.id ?? dataset.label;
                    try {
                        const resolved = await resolveColor?.(key, index);
                        return resolved ?? fallbackColors[index % fallbackColors.length];
                    } catch {
                        return fallbackColors[index % fallbackColors.length];
                    }
                }),
            );
            if (!cancelled) setLegendColors(colors);
        };
        buildLegendColors();
        return () => {
            cancelled = true;
        };
    }, [data, resolveColor]);

    const handleToggleDataset = (datasetIndex: number) => {
        const chart = chartRef.current;
        if (!chart) return;
        const meta = chart.getDatasetMeta(datasetIndex);
        const currentlyVisible = chart.isDatasetVisible(datasetIndex);
        meta.hidden = currentlyVisible ? true : false;
        chart.update();
        // 凡例のチェック状態とテキスト色を即時反映させるため再描画を促す
        setLegendRefreshTick((v) => v + 1);
    };

    const noDataLabel = noDataMessage ?? t('chart.noData');

    if (!data || !data.datasets.length) {
        return (
            <div className="flex h-full flex-col items-center justify-center">
                <img src={chartIcon} alt={noDataLabel} className="w-20 h-20 image-protection" />
                <p className="mt-4 text-xl">{noDataLabel}</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col">
            <div className="relative flex items-center justify-end gap-2 px-2 pt-2 pb-1">
                {!hideLegendButton && (
                    <div className="relative inline-block">
                        <button
                            ref={legendButtonRef}
                            type="button"
                            className="px-2 py-1 text-xs rounded bg-white/80 hover:bg-white shadow border border-gray-200 flex items-center gap-1"
                            onClick={toggleLegend}
                            aria-pressed={effectiveShowLegend}
                        >
                            <span>{t('chart.legendColors', { defaultValue: '項目' })}</span>
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                                className={`transition-transform duration-150 ${effectiveShowLegend ? '-rotate-90' : ''}`}
                                aria-hidden="true"
                            >
                                <path
                                    d="M7 5l5 5-5 5"
                                    stroke="#374151"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        {effectiveShowLegend && (
                            <>
                                {/* クリック外し用オーバーレイ */}
                                <div
                                    style={{
                                        position: 'fixed',
                                        inset: 0,
                                        zIndex: 9998,
                                        background: 'transparent',
                                    }}
                                    onClick={toggleLegend}
                                    aria-hidden="true"
                                />
                                {/* レジェンドポップオーバー（ボタン直上に固定） */}
                                <div className="absolute right-0 bottom-full mb-2 z-[9999]">
                                    <div
                                        className="w-56 sm:w-80 rounded-md border border-gray-200 bg-white/95 shadow overflow-auto"
                                        style={{ maxHeight: '60vh' }}
                                    >
                                        <ul className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                                            {data.datasets.map((ds, idx) => {
                                                const color = legendColors[idx] ?? '#cccccc';
                                                const visible =
                                                    chartRef.current?.isDatasetVisible(idx) ?? true;
                                                const key = ds.id ?? ds.label ?? String(idx);
                                                const labelText =
                                                    (ds.label?.trim()?.length ?? 0) > 0
                                                        ? ds.label
                                                        : t('chart.untitled');
                                                return (
                                                    <li
                                                        key={key}
                                                        className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-50 rounded border border-gray-200"
                                                        onClick={() => handleToggleDataset(idx)}
                                                        title={labelText}
                                                    >
                                                        <span
                                                            className="inline-block h-3 w-3 rounded"
                                                            style={{ backgroundColor: color }}
                                                            aria-hidden="true"
                                                        />
                                                        <span
                                                            className={`text-sm truncate ${visible ? 'text-gray-800' : 'text-gray-400 line-through'}`}
                                                        >
                                                            {labelText}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            role="checkbox"
                                                            aria-checked={visible}
                                                            aria-label={`toggle-${labelText}`}
                                                            className="ms-auto h-6 w-6 flex items-center justify-center"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleDataset(idx);
                                                            }}
                                                        >
                                                            <svg
                                                                width="24"
                                                                height="24"
                                                                viewBox="0 0 20 20"
                                                                className={
                                                                    visible
                                                                        ? 'text-gray-800'
                                                                        : 'text-gray-300'
                                                                }
                                                                aria-hidden="true"
                                                            >
                                                                <path
                                                                    d="M5 10l3 3 7-7"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                {!hideSwitchButton && (
                    <button
                        type="button"
                        aria-label="switch-to-donut"
                        className="px-2 py-1 text-xs rounded bg-white/80 hover:bg-white shadow border border-gray-200"
                        onClick={() => onSwitchToDonut?.()}
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M12 2v10l8.49 4.9A10 10 0 1 1 12 2z" fill="#9CA3AF" />
                            <path d="M14 12h8A10 10 0 0 1 12 22v-8a2 2 0 0 0 2-2z" fill="#D1D5DB" />
                        </svg>
                    </button>
                )}
                {/* 外部アンカー（SimpleAnalyticsPanel側のボタン）用のポータル描画 */}
                {effectiveShowLegend &&
                    hideLegendButton &&
                    createPortal(
                        (() => {
                            const anchorEl = (legendAnchorRef && legendAnchorRef.current) || null;
                            if (!anchorEl) return null as any;
                            const rect = anchorEl.getBoundingClientRect();
                            const viewportH =
                                typeof window !== 'undefined' ? window.innerHeight : 800;
                            const spaceAbove = rect.top - 12;
                            const spaceBelow = viewportH - rect.bottom - 12;
                            const placeAbove = spaceAbove >= 180 || spaceAbove >= spaceBelow;
                            const maxH = Math.max(
                                140,
                                Math.min(
                                    Math.floor(viewportH * 0.6),
                                    Math.floor(placeAbove ? spaceAbove : spaceBelow),
                                ),
                            );
                            const scrollX = typeof window !== 'undefined' ? window.scrollX : 0;
                            const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;
                            const commonPos = {
                                position: 'absolute' as const,
                                left: rect.right + scrollX,
                                zIndex: 9999,
                            };
                            const style = placeAbove
                                ? {
                                      ...commonPos,
                                      top: rect.top + scrollY,
                                      transform: 'translate(-100%, calc(-100% - 12px))',
                                  }
                                : {
                                      ...commonPos,
                                      top: rect.bottom + scrollY + 12,
                                      transform: 'translateX(-100%)',
                                  };
                            return (
                                <>
                                    <div
                                        style={{
                                            position: 'fixed',
                                            inset: 0,
                                            zIndex: 9998,
                                            background: 'transparent',
                                        }}
                                        onClick={toggleLegend}
                                        aria-hidden="true"
                                    />
                                    <div style={style} data-pos-tick={positionTick}>
                                        <div
                                            className="w-56 sm:w-80 rounded-md border border-gray-200 bg-white/95 shadow overflow-auto"
                                            style={{ maxHeight: `${maxH}px` }}
                                        >
                                            <ul className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                                                {data.datasets.map((ds, idx) => {
                                                    const color = legendColors[idx] ?? '#cccccc';
                                                    const visible =
                                                        chartRef.current?.isDatasetVisible(idx) ??
                                                        true;
                                                    const key = ds.id ?? ds.label ?? String(idx);
                                                    const labelText =
                                                        (ds.label?.trim()?.length ?? 0) > 0
                                                            ? ds.label
                                                            : t('chart.untitled');
                                                    return (
                                                        <li
                                                            key={key}
                                                            className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-50 rounded border border-gray-200"
                                                            onClick={() => handleToggleDataset(idx)}
                                                            title={labelText}
                                                        >
                                                            <span
                                                                className="inline-block h-3 w-3 rounded"
                                                                style={{ backgroundColor: color }}
                                                                aria-hidden="true"
                                                            />
                                                            <span
                                                                className={`text-sm truncate ${visible ? 'text-gray-500' : 'text-gray-400 line-through'}`}
                                                            >
                                                                {labelText}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                role="checkbox"
                                                                aria-checked={visible}
                                                                aria-label={`toggle-${labelText}`}
                                                                className="ms-auto h-6 w-6 flex items-center justify-center"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleToggleDataset(idx);
                                                                }}
                                                            >
                                                                <svg
                                                                    width="20"
                                                                    height="20"
                                                                    viewBox="0 0 20 20"
                                                                    className={
                                                                        visible
                                                                            ? 'text-gray-500'
                                                                            : 'text-gray-300'
                                                                    }
                                                                    aria-hidden="true"
                                                                >
                                                                    <path
                                                                        d="M5 10l3 3 7-7"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    </div>
                                </>
                            );
                        })(),
                        document.body,
                    )}
            </div>

            <div
                ref={scrollRef}
                className="relative flex-1 min-h-0 overflow-x-auto overflow-y-hidden"
            >
                <div
                    className="inline-block h-full align-top"
                    style={{ width: innerWidthPx, minWidth: minRequiredWidthPx || undefined }}
                >
                    <canvas ref={canvasRef} className="block h-full w-full max-w-none" />
                </div>
            </div>
            {minRequiredWidthPx > containerWidth && containerWidth > 0 ? (
                <p className="mt-1 px-2 text-center text-xs text-gray-400">
                    {t('chart.scrollRight', { defaultValue: '右にスクロールできます →' })}
                </p>
            ) : null}
        </div>
    );
};

export default StackedVerticalBarChart;
